/**
 * shipPhysics.ts
 * Fixed-step physics for the Courier Ship (World 2 space shmup).
 *
 * Reads `inputState` (left / right / up / down), writes velocity and position
 * on a passed-in {@link ShipEntity}. Clamps the ship inside the visible
 * viewport — the viewport's left edge is the camera X, so as the camera
 * scrolls right the playable window slides with it.
 *
 * NO React imports, NO Reanimated imports, NO async — engine-internal only.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module shipPhysics
 */

import type { ShipEntity } from '../entities/ShipEntity';
import { inputState } from '../input/inputState';
import { VIEWPORT_WIDTH, VIEWPORT_HEIGHT } from '../../constants/gameConstants';
import {
  SHIP_ACCELERATION,
  SHIP_FRICTION,
  SHIP_MAX_SPEED,
} from '../../constants/spaceConstants';

/**
 * Apply one step of axis acceleration / friction toward the input direction.
 *
 * `dir` is -1 (negative input held), +1 (positive input held), or 0 (no input).
 * When `dir` is non-zero the velocity accelerates toward `±SHIP_MAX_SPEED`.
 * When `dir` is zero, friction pulls velocity toward 0 without overshooting.
 *
 * Pure: depends only on its inputs.
 */
function applyAxis(v: number, dir: -1 | 0 | 1, dt: number): number {
  if (dir !== 0) {
    const next = v + SHIP_ACCELERATION * dir * dt;
    return Math.max(-SHIP_MAX_SPEED, Math.min(SHIP_MAX_SPEED, next));
  }
  if (v > 0) return Math.max(0, v - SHIP_FRICTION * dt);
  if (v < 0) return Math.min(0, v + SHIP_FRICTION * dt);
  return 0;
}

/**
 * Resolve the directional input for one axis.
 *
 * DECISION: Both opposed inputs held (e.g. left+right) cancels out rather
 * than picking a winner. Matches how the platformer treats simultaneous
 * left+right (returns to friction).
 */
function axisDir(negative: boolean, positive: boolean): -1 | 0 | 1 {
  if (negative && !positive) return -1;
  if (positive && !negative) return 1;
  return 0;
}

/**
 * Execute one fixed-step physics update for the Courier Ship.
 *
 * Pipeline:
 *  1. Read directional input (`up`/`down`/`left`/`right`).
 *  2. Update vx/vy via acceleration or friction.
 *  3. Integrate position.
 *  4. Clamp the ship's top-left hitbox corner so the full sprite stays on
 *     screen: X in `[cameraX, cameraX + VIEWPORT_WIDTH - ship.width]`,
 *     Y in `[0, VIEWPORT_HEIGHT - ship.height]`.
 *
 * @param ship    - The ship to advance. Mutated in place.
 * @param dt      - Fixed timestep in seconds (always FIXED_STEP).
 * @param cameraX - Left edge of the visible window in world-space px.
 *                  Defaults to 0 so unit tests can omit it.
 */
export function stepShipPhysics(ship: ShipEntity, dt: number, cameraX: number = 0): void {
  // ── 1. Resolve input → axis direction ──────────────────────────────────────
  const dx = axisDir(inputState.left, inputState.right);
  const dy = axisDir(inputState.up, inputState.down);

  // ── 2. Update velocity ────────────────────────────────────────────────────
  ship.vx = applyAxis(ship.vx, dx, dt);
  ship.vy = applyAxis(ship.vy, dy, dt);

  // ── 3. Integrate position ──────────────────────────────────────────────────
  ship.x += ship.vx * dt;
  ship.y += ship.vy * dt;

  // ── 4. Clamp to viewport ──────────────────────────────────────────────────
  const minX = cameraX;
  const maxX = cameraX + VIEWPORT_WIDTH - ship.width;
  if (ship.x < minX) {
    ship.x = minX;
    if (ship.vx < 0) ship.vx = 0;
  } else if (ship.x > maxX) {
    ship.x = maxX;
    if (ship.vx > 0) ship.vx = 0;
  }

  const maxY = VIEWPORT_HEIGHT - ship.height;
  if (ship.y < 0) {
    ship.y = 0;
    if (ship.vy < 0) ship.vy = 0;
  } else if (ship.y > maxY) {
    ship.y = maxY;
    if (ship.vy > 0) ship.vy = 0;
  }
}
